/**
 * AppShell — sidebar 304 px + topbar 80 px (bible §4.1, §6), styles app.css.
 * TOUT est dérivé du registre : aucun libellé de module, aucune route en dur
 * (verrouillé par registry.invariants.test.ts).
 */
import { NAV_GROUP_LABELS, profilMenu } from "@geceram/shared";
import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import type { Me } from "@geceram/shared";
import { aide } from "./aide/aides";
import { Icon } from "./icons";
import { LienNav, LienPied } from "./LienNav";
import { NavReduite } from "./NavReduite";
import { Palette } from "./ux/Palette";
import { navReduiteFor, navTreeFor, registry } from "./registry";
import { liensPied } from "./accueil";

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function Shell({ me, devStub }: { me: Me; devStub: boolean }) {
  const location = useLocation();
  // F1/F2 (relecture finale) : Estelle (secretariat + permission `arbitrage`)
  // doit garder le menu complet (spec C1) — le rôle seul ne suffit plus à
  // décider. `navTreeFor` n'est calculé QUE dans la branche menu complet
  // (F10) : le calculer toujours puis le jeter dans la branche réduite
  // faisait tourner l'arbre complet pour rien à chaque rendu d'Élodie.
  const profil = profilMenu(me.roles, me.permissions);
  const crumb = location.pathname.split("/").filter(Boolean);
  /**
   * Menu mobile. Sous 767 px la barre laterale est masquee par app.css ;
   * sans ce menu, AUCUN des 14 modules n'etait atteignable au doigt
   * (defaut trouve le 2026-08-11). On reutilise la barre existante plutot
   * que de dupliquer la navigation : un module ajoute demain y apparait
   * seul, avec les memes droits par role.
   */
  const [menuOuvert, setMenuOuvert] = useState(false);

  // Naviguer FERME le menu : sinon il resterait ouvert par-dessus la page
  // qu'on vient de demander.
  useEffect(() => {
    setMenuOuvert(false);
  }, [location.pathname]);

  // Echap ferme, et la page dessous cesse de defiler tant que c'est ouvert.
  useEffect(() => {
    if (!menuOuvert) return;
    const auClavier = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOuvert(false);
    };
    document.body.classList.add("nav-ouverte");
    window.addEventListener("keydown", auClavier);
    return () => {
      document.body.classList.remove("nav-ouverte");
      window.removeEventListener("keydown", auClavier);
    };
  }, [menuOuvert]);

  return (
    <div className="app">
      <Palette />
      <aside className={menuOuvert ? "side ouvert" : "side"} id="nav-principale">
        <div className="side-head with-logo">
          {/* Variante BLANCHE : la barre latérale est sombre dans les deux
              thèmes (`--nav-950`), le logo d'origine — noir — y serait
              invisible. Même dessin, même fichier source, seule la teinte du
              masque change. */}
          <img className="logo-gc" src="/logos/geceram-blanc.png" alt="GÉCÉRAM" />
          <span>{devStub ? "session locale (dev)" : me.name}</span>
        </div>
        <button
          className="side-search"
          type="button"
          onClick={() => window.dispatchEvent(new Event("geceram:palette"))}
          {...aide("coque.recherche")}
        >
          <Icon id="i-search" size="sm" />
          <span>Rechercher…</span>
          <kbd>Ctrl K</kbd>
        </button>
        <nav className="nav" aria-label="Navigation principale">
          {profil ? (
            <NavReduite nav={navReduiteFor(me.roles, profil)} onClick={() => setMenuOuvert(false)} />
          ) : (
            navTreeFor(me.roles).map(({ group, modules }) => (
              <div key={group}>
                <div className="grp">{NAV_GROUP_LABELS[group]}</div>
                {modules.map((mod) => (
                  <LienNav
                    key={mod.id}
                    mod={mod}
                    /* Ferme le menu mobile. L'effet sur `pathname` ne suffit
                       PAS : cliquer le lien de la page COURANTE ne change pas
                       l'URL, donc le menu restait ouvert par-dessus l'écran —
                       mesuré le 2026-08-11. */
                    onClick={() => setMenuOuvert(false)}
                  />
                ))}
              </div>
            ))
          )}
        </nav>
        {/*
          Accès direct au portail Vevidental. Il ne peut PAS être intégré dans
          une fenêtre du CRM : leur serveur l'interdit (`x-frame-options:
          SAMEORIGIN`, mesuré le 2026-08-08). Un onglet séparé est donc le seul
          accès possible — et il est ici plutôt que nulle part, parce que la
          saisie continue de se faire chez eux.
          `rel="noopener noreferrer"` : sans lui, la page ouverte garde une
          prise sur la nôtre via `window.opener`.
        */}
        <a
          className="side-vevi"
          href="https://www.vevidental.com/GeCeram/home"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setMenuOuvert(false)}
          {...aide("nav.vevidentalSite")}
        >
          <img src="/logos/vevidental.svg" alt="" aria-hidden="true" height={18} />
          {/* « Portail » et non « Vevidental » : le module du même nom, juste
              au-dessus, montre les données rapatriées. Deux entrées homonymes
              dans une même barre feraient hésiter avant chaque clic. */}
          <span>Portail Vevidental</span>
        </a>
        <div className="side-foot">
          <div className="av">{initials(me.name)}</div>
          <div className="who">
            <b>{me.name}</b>
            <span>{me.email}</span>
          </div>
          {/*
            Les modules qui ont demandé à vivre ICI plutôt que dans la barre
            (`lienPied`). Rien n'est nommé : la liste est DÉRIVÉE du registre,
            filtrée par les rôles de la personne. Un module ajouté demain y
            apparaît seul — voir `ModuleDef.lienPied`.
          */}
          {liensPied(registry, me.roles).map((m) => (
            <LienPied key={m.id} mod={m} />
          ))}
          <a className="ib" href="/api/v1/auth/logout" aria-label="Déconnexion" {...aide("coque.deconnexion")}>
            <Icon id="i-logout" size="sm" />
          </a>
        </div>
      </aside>

      <div className="main">
        <header className="top">
          <button
            className="iconbtn menu"
            type="button"
            aria-label={menuOuvert ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={menuOuvert}
            aria-controls="nav-principale"
            onClick={() => setMenuOuvert((v) => !v)}
            {...aide("coque.menu")}
          >
            <Icon id={menuOuvert ? "i-x" : "i-menu"} size="sm" />
          </button>
          <div className="crumb">
            {crumb.length === 0 ? <b>Accueil</b> : crumb.map((c, i) => (
              i === crumb.length - 1 ? <b key={c}>{decodeURIComponent(c)}</b> : (
                <span key={c}>
                  <span>{decodeURIComponent(c)}</span>
                  <span className="sep"> › </span>
                </span>
              )
            ))}
          </div>
          <button
            className="iconbtn"
            type="button"
            aria-label="Basculer le thème"
            onClick={() => {
              const h = document.documentElement;
              h.dataset["theme"] = h.dataset["theme"] === "dark" ? "light" : "dark";
            }}
            {...aide("coque.theme")}
          >
            <Icon id="i-moon" size="sm" />
          </button>
        </header>
        {/* Environnement d'essai : Twenty y est coupé PAR CHOIX (drapeau servi
            par /api/v1/me). Dit UNE fois ici, pour tous les écrans — sans lui,
            leurs erreurs se lisent comme une panne (vécu Dove, 31/08). */}
        {me.twentyCoupe ? (
          <div className="banner warn" role="status" style={{ margin: "12px 24px 0" }}>
            <Icon id="i-alert" size="sm" />
            <span>
              Twenty est coupé sur cet environnement d'essai — tableau de bord, cabinets, clients,
              relances et recherche afficheront une erreur : c'est voulu, rien n'est cassé.
            </span>
          </div>
        ) : null}
        <Outlet />
      </div>

      {/* Voile : un appui a cote ferme le menu — le geste attendu au doigt. */}
      {menuOuvert ? (
        <button
          className="voile-nav"
          type="button"
          aria-label="Fermer le menu"
          onClick={() => setMenuOuvert(false)}
          /* `coque.voile`, pas `coque.menu` : le voile ne fait que FERMER, là
             où l'aide du bouton dit « ouvre ou ferme ». */
          {...aide("coque.voile")}
        />
      ) : null}
    </div>
  );
}
